"use client";
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/ui';

const alertVariants = cva(
  'relative w-full items-start gap-y-0.5 rounded-lg border px-4 py-3 text-sm ' +
    '[&>svg+div]:translate-y-[-3px] ' +
    '[&>svg]:absolute [&>svg]:left-4 [&>svg]:top-2 [&>svg]:text-current ' +
    '[&>svg~*]:pl-7',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground [&_[data-alert-description]]:text-primary-foreground/90',
        secondary: 'bg-secondary text-secondary-foreground [&_[data-alert-description]]:text-secondary-foreground/90',
        success: 'bg-success text-success-foreground [&_[data-alert-description]]:text-success-foreground/90',
        warning: 'bg-warning text-warning-foreground [&_[data-alert-description]]:text-warning-foreground/90',
        danger: 'bg-danger text-danger-foreground [&_[data-alert-description]]:text-danger-foreground/90',
        info: 'bg-info text-info-foreground [&_[data-alert-description]]:text-info-foreground/90',
        light: 'bg-light text-light-foreground [&_[data-alert-description]]:text-light-foreground/90',
        dark: 'bg-dark text-dark-foreground [&_[data-alert-description]]:text-dark-foreground/90',
        accent: 'bg-accent text-accent-foreground [&_[data-alert-description]]:text-accent-foreground/90',
        destructive:
          'bg-destructive text-destructive-foreground [&_[data-alert-description]]:text-destructive-foreground/90',
        muted: 'bg-muted text-muted-foreground [&_[data-alert-description]]:text-muted-foreground/90',
        link: 'text-primary underline-offset-4 [&_[data-alert-description]]:text-primary/90',
        ghost: '[&_[data-alert-description]]:text-muted-foreground',
      },
      appearance: {
        solid: '',
        light: 'font-semibold',
      },
    },
    compoundVariants: [
      {
        variant: 'primary',
        appearance: 'light',
        class: 'bg-primary/20 text-primary [&_[data-alert-description]]:text-primary',
      },
      {
        variant: 'secondary',
        appearance: 'light',
        class: 'bg-secondary/20 text-secondary [&_[data-alert-description]]:text-secondary',
      },
      {
        variant: 'success',
        appearance: 'light',
        class: 'bg-success/20 text-success [&_[data-alert-description]]:text-success',
      },
      {
        variant: 'warning',
        appearance: 'light',
        class: 'bg-warning/20 text-warning [&_[data-alert-description]]:text-warning',
      },
      {
        variant: 'danger',
        appearance: 'light',
        class: 'bg-danger/20 text-danger [&_[data-alert-description]]:text-danger',
      },
      { variant: 'info', appearance: 'light', class: 'bg-info/20 text-info [&_[data-alert-description]]:text-info' },
      {
        variant: 'light',
        appearance: 'light',
        class: 'bg-light/20 text-light [&_[data-alert-description]]:text-light',
      },
      { variant: 'dark', appearance: 'light', class: 'bg-dark/20 text-dark [&_[data-alert-description]]:text-dark' },
      {
        variant: 'accent',
        appearance: 'light',
        class: 'bg-accent/20 text-accent [&_[data-alert-description]]:text-accent',
      },
      {
        variant: 'destructive',
        appearance: 'light',
        class: 'bg-destructive/20 text-destructive [&_[data-alert-description]]:text-destructive',
      },
      {
        variant: 'muted',
        appearance: 'light',
        class: 'bg-muted/20 text-muted [&_[data-alert-description]]:text-muted',
      },
      {
        variant: 'link',
        appearance: 'light',
        class: 'bg-primary/10 text-primary [&_[data-alert-description]]:text-primary',
      },
      {
        variant: 'ghost',
        appearance: 'light',
        class: 'bg-muted/10 text-muted-foreground [&_[data-alert-description]]:text-muted-foreground',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      appearance: 'solid',
    },
  },
);

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {}
export type VariantType = NonNullable<VariantProps<typeof alertVariants>['variant']>;

function Alert({ className, variant, ...props }: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return <div data-slot="alert" role="alert" className={cn(alertVariants({ variant }), className)} {...props} />;
}
function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'font-medium group-has-[>svg]/alert:col-start-2 cn-font-heading [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
}
function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-sm text-balance md:text-pretty [&_p:not(:last-child)]:mb-4 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
}
function AlertAction({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="alert-action" className={cn('absolute top-2 right-2', className)} {...props} />;
}
export { Alert, AlertTitle, AlertDescription, AlertAction };
